import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BellRing,
  KeyRound,
  Loader2,
  RotateCcw,
  RotateCw,
  Trash2,
  UserCog,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { AccessQueue } from "@/components/AccessRequests";
import { SortHeader } from "@/components/SortHeader";
import { sortRows, useSort } from "@/lib/sorting";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, fmtWhen } from "@/lib/api";
import { PersonFormDialog } from "@/components/PersonFormDialog";
import { UnitsPicker } from "@/components/UnitsPicker";
import { useAuth } from "@/context/AuthContext";

const StatusPill = ({ status }) => {
  const map = {
    active: "border-[hsl(var(--healthy))] text-[hsl(var(--healthy))]",
    invited: "border-[hsl(var(--watch))] text-[hsl(var(--watch))]",
    deactivated: "border-muted-foreground text-muted-foreground",
  };
  const label = { active: "Signed up", invited: "Invited", deactivated: "Switched off" };
  return (
    <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${map[status]}`}>
      {label[status] || status}
    </span>
  );
};

export const AccessPanel = ({ inviteSignal = 0 }) => {
  const { user: me, shows, writable } = useAuth();
  const [users, setUsers] = useState([]);
  const { sort, on: sortBy } = useSort("users");
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "spoc", plant: "none", designation: "", units: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [mail, setMail] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [passcode, setPasscode] = useState(null);
  const [unitsFor, setUnitsFor] = useState(null);
  const [options, setOptions] = useState(null);
  const [personFor, setPersonFor] = useState(null);
  const [q, setQ] = useState("");
  const [fRole, setFRole] = useState("all");
  const [fUnit, setFUnit] = useState("all");
  const [fStatus, setFStatus] = useState("all");

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/users/stats").then((r) => setStats(r.data));
    api.get("/settings/reminders").then((r) => setReminder(r.data));
    api.get("/users/mail-health").then((r) => setMail(r.data)).catch(() => setMail(null));
  };

  /** One form for the whole person — their card in the directory and what they can do. */
  const openPerson = (u) => {
    setPersonFor(
      u
        ? {
            user_id: u.id,
            card_id: u.card?.id || null,
            name: u.name || "",
            email: u.email || "",
            plant: u.plant || "",
            band: u.card?.band || "",
            working_title: u.card?.working_title || "",
            lead_units: u.card?.lead_units || [],
            department: u.department || u.card?.department || "",
            designation: u.designation || u.card?.designation || "",
            designation_grade: u.designation_grade || "",
            sail_id: u.personnel_no || u.card?.sail_id || "",
            contact_number: u.mobile || u.card?.contact_number || "",
            whatsapp_number: u.card?.whatsapp_number || "",
            landline: u.landline || u.card?.landline || "",
            stationed_at: u.stationed_at || u.card?.stationed_at || "",
            org_category: u.org_category || "",
            org_unit: u.org_unit || "",
            role: u.role || "",
            units: u.units || [],
            access_status: u.status === "deactivated" ? "deactivated" : "active",
          }
        : { plant: me?.plant || "", band: "", role: "", units: [] },
    );
  };

  useEffect(() => {
    if (!inviteSignal) return;
    setOpen(true);
  }, [inviteSignal]);

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied.`);
    } catch {
      toast.error("Could not copy — please select the text and copy it by hand.");
    }
  };

  const handPasscode = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/passcode`);
      setPasscode(data);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resetPassword = async (u) => {
    if (
      !window.confirm(
        `Reset the password of ${u.name || u.email} back to "${u.email.split("@")[0]}"? ` +
          "They will have to choose their own the next time they use it.",
      )
    )
      return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setPasscode({
        name: data.name,
        email: data.email,
        code: data.password,
        ttl_minutes: null,
        is_password: true,
        mailed: data.email_sent,
      });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/profile/options").then((r) => setOptions(r.data)).catch(() => setOptions(null));
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api
        .get("/users/directory-suggestions", { params: { q: form.email || form.name } })
        .then((r) => setSuggestions(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [open, form.email, form.name]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users/invite", {
        ...form,
        plant: form.plant === "none" ? null : form.plant,
        units: form.role === "admin" ? form.units : [],
      });
      data.email_sent
        ? toast.success(`Invitation mailed to ${data.user.email}`)
        : toast.error(`Access created but the mail failed: ${data.email_error}`);
      setOpen(false);
      setForm({ email: "", name: "", role: "spoc", plant: "none", designation: "", units: [] });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const runReminders = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/settings/reminders/run");
      toast.success(data.summary);
      const { data: fresh } = await api.get("/settings/reminders");
      setReminder(fresh);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRunning(false);
    }
  };

  const sendTestMail = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/users/mail-test", null, {
        params: testTo.trim() ? { to: testTo.trim() } : {},
      });
      if (data.sent) toast.success(`Test mail sent to ${data.to} over the ${data.transport}.`);
      else toast.error(`Could not send to ${data.to} — ${data.error}`);
      api.get("/users/mail-health").then((r) => setMail(r.data)).catch(() => {});
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTesting(false);
    }
  };

  // The slicers narrow the table so one account can be found and corrected without scrolling.
  const needle = q.trim().toLowerCase();
  const shown = users.filter((u) => {
    if (fRole !== "all" && u.role !== fRole) return false;
    if (fUnit !== "all" && (u.plant || "") !== fUnit) return false;
    if (fStatus === "never" && u.last_login_at) return false;
    if (fStatus !== "all" && fStatus !== "never" && u.status !== fStatus) return false;
    if (!needle) return true;
    return [u.name, u.email, u.department, u.designation, u.org_unit].some((v) =>
      String(v || "").toLowerCase().includes(needle),
    );
  });
  const filtered = needle || fRole !== "all" || fUnit !== "all" || fStatus !== "all";

  return (
    <div data-testid="access-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Portal access</div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Invite SPOCs and SDTD officers, decide what each of them can do, hand a passcode over
            the phone or put a password back. Every person on the portal is edited from the one
            person form — who they are, where they sit, their team and what they can do.
          </p>
          {me?.role === "system_admin" && (
            <Button
              className="mt-4 h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
              data-testid="add-person-button"
              onClick={() => openPerson(null)}
            >
              <UserPlus className="mr-2 h-4 w-4" /> Add a person
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-11 rounded-none"
            data-testid="run-reminders-button"
            disabled={running}
            onClick={runReminders}
          >
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BellRing className="mr-2 h-4 w-4" />}
            Send reminders now
          </Button>
          {shows("users.invite") && writable("users") && (
            <Button
              data-testid="open-invite-dialog"
              onClick={() => setOpen(true)}
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              <UserPlus className="mr-2 h-4 w-4" /> Invite someone
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-px bg-border lg:grid-cols-6">
        {[
          ["Total accounts", stats?.total, "user-stat-total"],
          ["Signed up", stats?.active, "user-stat-active"],
          ["Invitation pending", stats?.invited, "user-stat-invited"],
          ["Switched off", stats?.deactivated, "user-stat-deactivated"],
          ["SPOCs", stats?.spocs, "user-stat-spocs"],
          ["Away 3+ days", stats?.not_signed_in_3_days, "user-stat-away"],
        ].map(([label, value, tid]) => (
          <div key={label} className="bg-card p-4" data-testid={tid}>
            <div className="label-caps">{label}</div>
            <div className="font-data mt-2 text-2xl font-bold">{value ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="border border-border bg-card p-4 text-xs" data-testid="reminder-state">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="label-caps">Automatic reminders</div>
            <Link
              to="/admin/settings"
              data-testid="reminder-settings-link"
              className="font-data text-[10px] uppercase tracking-widest text-primary transition-opacity duration-200 hover:opacity-70"
            >
              Change settings
            </Link>
          </div>
          <p className="mt-1 text-muted-foreground">
            {reminder?.settings?.enabled === false
              ? "Automated reminders are switched off right now."
              : `Every day at ${String(reminder?.settings?.hour ?? 9).padStart(2, "0")}:${String(
                  reminder?.settings?.minute ?? 0,
                ).padStart(2, "0")} IST the portal mails a SPOC who has not signed in for ${
                  reminder?.settings?.login_reminder_days ?? 3
                } days, mails a SPOC whose unit register has not been updated for ${
                  reminder?.settings?.update_reminder_days ?? 7
                } days, and sends SDTD a digest of who is lagging.`}
          </p>
          {reminder?.last_run?.last_run_at && (
            <p className="font-data mt-2">
              Last run {fmtWhen(reminder.last_run.last_run_at)} · {reminder.last_run.summary}
            </p>
          )}
        </div>

        <div className="border border-border bg-card p-4 text-xs" data-testid="mail-health">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="label-caps">How the portal's mail is going out</div>
            <span
              className="font-data border px-2 py-0.5 text-[10px] uppercase tracking-widest"
              style={{
                borderColor:
                  mail?.transport === "smtp" ? "hsl(var(--healthy))" : "hsl(var(--watch))",
                color: mail?.transport === "smtp" ? "hsl(var(--healthy))" : "hsl(var(--watch))",
              }}
              data-testid="mail-transport"
            >
              {mail?.transport === "smtp" ? "gmail relay" : "own domain (SPF + DKIM)"}
            </span>
          </div>
          <p className="mt-1 text-muted-foreground">
            {mail?.transport === "smtp"
              ? "Mail goes out through the Gmail relay, which SAIL's gateway accepts. The portal's own domain is kept as the standby."
              : "Mail is going from the portal's own domain — SAIL's gateway refuses it with a 554 policy violation, so switch back to the Gmail relay."}{" "}
            Last {mail?.recent?.length ?? 0} attempts: {mail?.sent ?? 0} accepted,{" "}
            {mail?.failed ?? 0} failed.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Input
              className="h-9 max-w-[240px] rounded-none text-xs"
              placeholder="officer@sail.in"
              data-testid="mail-test-address"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <Button
              variant="outline"
              className="h-9 rounded-none text-xs"
              disabled={testing}
              data-testid="send-test-mail"
              onClick={sendTestMail}
            >
              {testing ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
              Send a test mail
            </Button>
          </div>
          <div className="font-data mt-2 max-h-24 space-y-0.5 overflow-y-auto text-[10px]">
            {(mail?.recent || []).slice(0, 6).map((m, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span className="truncate">
                  {m.sent ? "✓" : "✗"} {m.to?.join(", ")} — {m.subject}
                </span>
                <span className="shrink-0 text-muted-foreground">{fmtWhen(m.at)}</span>
              </div>
            ))}
            {!mail?.recent?.length && <span className="text-muted-foreground">Nothing yet.</span>}
          </div>
        </div>
      </div>

      <AccessQueue />

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="user-slicers">
        <Input
          className="h-11 w-[250px] rounded-none"
          placeholder="Name, e-mail, department"
          data-testid="user-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select value={fRole} onValueChange={setFRole}>
          <SelectTrigger className="h-11 w-[200px] rounded-none bg-card" data-testid="user-filter-role">
            <SelectValue placeholder="What they can do" />
          </SelectTrigger>
          <SelectContent className="max-h-72 rounded-none bg-popover">
            <SelectItem value="all">Any role</SelectItem>
            {(meta?.roles || []).map((r) => (
              <SelectItem key={r} value={r}>
                {meta?.role_labels?.[r] || r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fUnit} onValueChange={setFUnit}>
          <SelectTrigger className="h-11 w-[180px] rounded-none bg-card" data-testid="user-filter-unit">
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent className="max-h-72 rounded-none bg-popover">
            <SelectItem value="all">Any unit</SelectItem>
            {(meta?.plants || []).map((p) => (
              <SelectItem key={p.code} value={p.code}>
                {p.code} — {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-11 w-[200px] rounded-none bg-card" data-testid="user-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="active">Signed up</SelectItem>
            <SelectItem value="invited">Invitation pending</SelectItem>
            <SelectItem value="deactivated">Switched off</SelectItem>
            <SelectItem value="never">Never signed in</SelectItem>
          </SelectContent>
        </Select>
        {filtered && (
          <button
            type="button"
            data-testid="user-filter-clear"
            onClick={() => {
              setQ("");
              setFRole("all");
              setFUnit("all");
              setFStatus("all");
            }}
            className="h-11 border border-border px-3 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
          >
            Clear the slicers
          </button>
        )}
        <span className="font-data text-[11px] text-muted-foreground" data-testid="users-showing">
          Showing {shown.length} of {users.length} account(s)
        </span>
      </div>

      <div className="mt-6 overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[980px] text-sm" data-testid="users-table">
          <thead className="bg-muted">
            <tr className="text-left">
              {[
                ["name", "Person"],
                ["role", "What they can do"],
                ["status", "Status"],
                ["plant", "Unit"],
                ["invite_email_status", "Invitation"],
                ["last_login_at", "Last signed in"],
              ].map(([column, label]) => (
                <th key={column} className="label-caps px-4 py-3 font-bold">
                  <SortHeader label={label} column={column} sort={sort} onSort={sortBy} />
                </th>
              ))}
              <th className="label-caps px-4 py-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortRows(shown, sort.key, sort.dir).map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="transition-colors duration-200 hover:bg-muted">
                <td className="px-4 py-3">
                  <div className="font-semibold">{u.name}</div>
                  <div className="font-data text-[11px] text-muted-foreground">{u.email}</div>
                  {(u.designation || u.department || u.org_unit) && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`user-mapping-${u.email}`}>
                      {[u.designation, u.department, u.org_unit].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Select
                    value={u.role}
                    onValueChange={(v) => act(() => api.patch(`/users/${u.id}`, { role: v }), "Updated")}
                    disabled={u.role === "system_admin" && me?.role !== "system_admin"}
                  >
                    <SelectTrigger className="h-9 w-[190px] rounded-none" data-testid={`role-select-${u.email}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-popover">
                      {(meta?.roles || []).map((r) => (
                        <SelectItem key={r} value={r}>
                          {meta?.role_labels?.[r] || r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={u.status} />
                </td>
                <td className="px-4 py-3 text-xs">
                  {u.role === "admin" ? (
                    <button
                      type="button"
                      className="text-left underline decoration-dotted transition-colors duration-200 hover:text-primary"
                      data-testid={`units-${u.email}`}
                      title="Attach this Monitor to a few units, or leave it on all of SAIL"
                      onClick={() => setUnitsFor({ ...u, picked: u.units || [] })}
                    >
                      {u.units?.length ? u.units.join(", ") : "All units"}
                    </button>
                  ) : (
                    u.plant || "All units"
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  {u.invite_email_status === "sent" && <span className="text-[hsl(var(--healthy))]">Mail sent</span>}
                  {u.invite_email_status === "failed" && (
                    <span className="text-[hsl(var(--at-risk))]" title={u.invite_email_error}>
                      Mail failed
                    </span>
                  )}
                  {u.invite_email_status === "not_required" && (
                    <span className="text-muted-foreground">Built in</span>
                  )}
                </td>
                <td className="font-data px-4 py-3 text-xs">
                  {u.last_login_at ? fmtWhen(u.last_login_at) : "Never"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      data-testid={`resend-invite-${u.email}`}
                      onClick={() =>
                        act(() => api.post(`/users/${u.id}/resend-invite`), `Invitation re-sent to ${u.email}`)
                      }
                    >
                      <RotateCw className="mr-1 h-3.5 w-3.5" /> Re-send
                    </Button>
                    {me?.role === "system_admin" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        data-testid={`edit-mapping-${u.email}`}
                        title="One form: who they are, where they sit, their team and what they can do"
                        onClick={() => openPerson(u)}
                      >
                        <UserCog className="mr-1 h-3.5 w-3.5" /> Edit person
                      </Button>
                    )}
                    {me?.role === "system_admin" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        data-testid={`passcode-${u.email}`}
                        title="Take out a sign-in passcode you can read out over the phone"
                        onClick={() => handPasscode(u)}
                      >
                        <KeyRound className="mr-1 h-3.5 w-3.5" /> Passcode
                      </Button>
                    )}
                    {me?.role === "system_admin" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        data-testid={`reset-password-${u.email}`}
                        title="Put the password back to the part of their email before the @"
                        onClick={() => resetPassword(u)}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Password
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      data-testid={`toggle-status-${u.email}`}
                      onClick={() =>
                        act(
                          () =>
                            api.patch(`/users/${u.id}`, {
                              status: u.status === "deactivated" ? "active" : "deactivated",
                            }),
                          "Updated",
                        )
                      }
                    >
                      {u.status === "deactivated" ? "Turn on" : "Turn off"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none text-[hsl(var(--at-risk))]"
                      data-testid={`delete-user-${u.email}`}
                      onClick={() => setConfirm(u)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-card sm:max-w-lg" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">
              Invite someone
            </DialogTitle>
            <DialogDescription>
              They get an email with a link. Only @sail.in addresses can be added.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={invite} className="space-y-4">
            <div>
              <label className="label-caps" htmlFor="inv-email">Email</label>
              <Input
                id="inv-email"
                type="email"
                required
                data-testid="invite-email-input"
                placeholder="officer@sail.in"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 h-11 rounded-none font-data"
              />
            </div>
            {suggestions.length > 0 && (
              <div className="border border-border">
                <div className="label-caps border-b border-border bg-muted px-3 py-2">
                  From the contact list
                </div>
                <div className="max-h-32 overflow-y-auto">
                  {suggestions.slice(0, 6).map((s) => (
                    <button
                      key={s.email}
                      type="button"
                      data-testid={`suggestion-${s.email}`}
                      onClick={() =>
                        setForm({
                          ...form,
                          email: s.email,
                          name: s.name,
                          plant: s.plant || "none",
                          designation: s.designation || "",
                        })
                      }
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors duration-200 hover:bg-muted"
                    >
                      <span className="font-medium">{s.name}</span>
                      <span className="font-data text-muted-foreground">{s.email}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="label-caps" htmlFor="inv-name">Full name</label>
              <Input
                id="inv-name"
                required
                data-testid="invite-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 h-11 rounded-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps">What they can do</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-popover">
                    {(meta?.roles || []).map((r) => (
                      <SelectItem key={r} value={r} disabled={r === "system_admin" && me?.role !== "system_admin"}>
                        {meta?.role_labels?.[r] || r}
                        {meta?.role_help?.[r] ? ` — ${meta.role_help[r]}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps">Unit</label>
                <Select
                  value={form.plant}
                  onValueChange={(v) => setForm({ ...form, plant: v })}
                  disabled={form.role === "admin"}
                >
                  <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="invite-plant-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-popover">
                    <SelectItem value="none">All units</SelectItem>
                    {meta?.plants?.map((p) => (
                      <SelectItem key={p.code} value={p.code}>
                        {p.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role === "admin" && (
              <div className="border border-border bg-muted p-3">
                <label className="label-caps">Units this Monitor looks after</label>
                <div className="mt-2">
                  <UnitsPicker
                    plants={meta?.plants || []}
                    value={form.units}
                    onChange={(units) => setForm({ ...form, units })}
                    testId="invite-units"
                  />
                </div>
              </div>
            )}
            <p className="border border-border bg-muted p-3 text-xs text-muted-foreground">
              A SPOC can update only their own unit's projects. An SDTD Monitor covers the
              whole of SAIL unless you attach them to a few units above. An SDTD Administrator can
              update everything and use the reports. Management sees everything but changes nothing.
              A Pravartanam Partner may use their own company mailbox and sees only the target-value
              use cases and the training pages.
            </p>
            <DialogFooter>
              <Button
                type="submit"
                disabled={busy}
                data-testid="send-invite-button"
                className="h-11 w-full rounded-none font-display text-xs font-bold uppercase tracking-widest"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!passcode} onOpenChange={(o) => !o && setPasscode(null)}>
        <DialogContent className="rounded-none bg-card sm:max-w-lg" data-testid="passcode-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">
              {passcode?.is_password ? "New password for " : "Sign-in passcode for "}
              {passcode?.name}
            </DialogTitle>
            <DialogDescription>
              {passcode?.is_password ? (
                <>
                  The password of {passcode?.email} is back to the portal's default. They will be
                  asked to choose their own the next time they use it
                  {passcode?.mailed ? ", and they have been told by mail" : ""}. This is on the
                  activity log.
                </>
              ) : (
                <>
                  Read this out to {passcode?.email} over the phone if the mail has not reached them.
                  It is valid for {passcode?.ttl_minutes} minutes and this action is on the activity
                  log.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-border bg-muted p-4 text-center">
              <div
                className={`font-data font-bold ${passcode?.is_password ? "text-2xl" : "text-4xl tracking-[0.3em]"}`}
                data-testid="passcode-value"
              >
                {passcode?.code}
              </div>
              {passcode?.fixed && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  This account uses the standing SDTD passcode.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-10 flex-1 rounded-none text-xs"
                data-testid="copy-passcode"
                onClick={() => copy(passcode.code, passcode.is_password ? "Password" : "Passcode")}
              >
                {passcode?.is_password ? "Copy the password" : "Copy the passcode"}
              </Button>
              {!passcode?.is_password && (
                <Button
                  variant="outline"
                  className="h-10 flex-1 rounded-none text-xs"
                  data-testid="copy-invite-text"
                  onClick={() => copy(passcode.invite_text, "Joining message")}
                >
                  Copy the joining message
                </Button>
              )}
            </div>
            <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap border border-border bg-background p-3 text-[11px] leading-relaxed">
              {passcode?.invite_text}
            </pre>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!unitsFor} onOpenChange={(o) => !o && setUnitsFor(null)}>
        <DialogContent className="rounded-none bg-card sm:max-w-lg" data-testid="units-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">
              Units {unitsFor?.name} looks after
            </DialogTitle>
            <DialogDescription>
              An SDTD Monitor covers the whole of SAIL by default. Attach them to a few units and
              they will see, update and report on only those.
            </DialogDescription>
          </DialogHeader>
          <UnitsPicker
            plants={meta?.plants || []}
            value={unitsFor?.picked || []}
            onChange={(picked) => setUnitsFor((u) => ({ ...u, picked }))}
            testId="edit-units"
          />
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setUnitsFor(null)}
            >
              Cancel
            </Button>
            <Button
              className="rounded-none font-display text-xs font-bold uppercase tracking-widest"
              data-testid="save-units"
              onClick={() =>
                act(
                  () => api.patch(`/users/${unitsFor.id}`, { units: unitsFor.picked }),
                  unitsFor.picked?.length
                    ? `${unitsFor.name} now looks after ${unitsFor.picked.join(", ")}`
                    : `${unitsFor.name} now covers the whole of SAIL`,
                ).finally(() => setUnitsFor(null))
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-none bg-card" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Remove this person?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.email} will lose access straight away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none"
              data-testid="confirm-delete-user"
              onClick={() =>
                act(() => api.delete(`/users/${confirm.id}`), `${confirm.email} removed`).finally(() =>
                  setConfirm(null),
                )
              }
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PersonFormDialog
        open={!!personFor}
        onOpenChange={(o) => !o && setPersonFor(null)}
        person={personFor}
        meta={meta}
        options={options}
        plants={meta?.plants || []}
        onSaved={load}
      />
    </div>
  );
};
